export type TimeRemaining = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
};

/**
 * Calculates the remaining time until the given end date.
 * Returns days, hours, minutes, seconds, and whether the timer has expired.
 */
export function calculateTimeRemaining(endDate: string | undefined): TimeRemaining {
  if (!endDate) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true };
  }

  const now = new Date().getTime();
  const end = new Date(endDate).getTime();
  const diff = end - now;

  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true };
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds, isExpired: false };
}

/**
 * Formats a number to always be two digits (e.g., 5 -> "05").
 */
export function formatTimeUnit(value: number): string {
  return value.toString().padStart(2, '0');
}

/**
 * Formats the time remaining.
 * Shows "X days" or "1 day" when days >= 1, otherwise "HH:MM:SS".
 */
export function formatTimeDisplay(time: TimeRemaining): string {
  if (time.days >= 1) {
    return time.days === 1 ? '1 day remaining' : `${time.days} days remaining`;
  }

  return `${formatTimeUnit(time.hours)}:${formatTimeUnit(time.minutes)}:${formatTimeUnit(time.seconds)}`;
}
